// The "View With Tax & Shipping Distributed" toggles' arithmetic: the
// proportional charge-spread rule the Point Click Care export applies once per
// invoice PER CHARGE, across every LINE ITEM it touched (D-40), in
// `backend/src/domain/exportBuilder.ts` (`docs/export.md` → Sales tax and
// shipping). The allocation itself is `allocate` / `largestBaseIndex` from
// `@ap/shared` — the export's own functions, imported, not copied (code review
// D-08, 2026-10-03). This module only converts the screen's dollars to cents on
// the way in and back on the way out.
//
// TWO CHARGES, TWO ALLOCATIONS. Tax and shipping are allocated SEPARATELY over
// the same bases and their shares added — never summed first and allocated once.
// Two exact allocations sum exactly, so the invoice total is unaffected either
// way; keeping them apart is what lets a row report its own Tax and its own
// Shipping as two figures a reviewer can tie back to the document, which is what
// the Line Items Summary's toggle reveals. `exportBuilder.ts` does the same, and
// its header comment says so.
//
// UNITS: this package holds money as JS-number DOLLARS (app/README.md's "Money
// is a string on the wire" — the client parses it to a `number`). The
// allocation runs in whole cents, in the shared code. Never run this division
// in floating dollars: that is how a one-cent remainder gets lost or
// duplicated, the historical defect `docs/export.md` → Sales tax records.

import { allocate, centsFromNumber, largestBaseIndex as largestBaseIndexCents } from '@ap/shared'
import { reconcilesCents } from './format'

/**
 * THE LARGEST ROW — the one a leftover cent lands on, and the one the odd cent
 * is absorbed onto after the allocation. `-1` on an empty list. Shared
 * `largestBaseIndex` over the bases in cents: the single largest unsigned base
 * wins, a tie to the lower `sequence`.
 */
export function largestBaseIndex(
  bases: readonly number[],
  sequences: readonly number[],
): number {
  return largestBaseIndexCents(bases.map(centsFromNumber), sequences)
}

/**
 * Spread `amount` (dollars) across `bases` (dollars), proportional to each
 * base's magnitude, in exact cents: shared `allocate`, in dollars. Every base
 * zero (or none) gives every share `0`; otherwise the shares sum to `amount`.
 * `bases` and `sequences` must be the same length and line up index for index.
 */
export function allocateChargeDollars(
  amount: number,
  bases: readonly number[],
  sequences: readonly number[],
): number[] {
  return allocate(centsFromNumber(amount), bases.map(centsFromNumber), sequences).map((cents) => cents / 100)
}

/**
 * One row this module can distribute charges across: a LINE's pre-charge
 * total in dollars, plus its own `line_number` — the same tie-break key
 * `exportBuilder.ts` uses on the line grain since D-40.
 *
 * `sequence` is optional, and a row that omits it ties at `0`. That only matters
 * when two rows land on the EXACT same pre-charge total and a rounding remainder
 * has to go to one of them: the invoice total is unaffected either way and the
 * remainder still lands somewhere, it can just be a different tied row than the
 * export would have picked, by at most one cent. Every caller in this package
 * DOES thread it — `InvoiceDetailModal.tsx` and `EditableInvoiceForm.tsx`
 * pass `line_number` into the GL Summary's rows, and both line-item tables
 * distribute over lines, which carry their own — so the fallback is a
 * type-level convenience, not a live gap.
 */
interface DistributableRow {
  total: number
  sequence?: number
}

/** What one row picked up: its two charge shares, and the total including them. */
interface ChargeShare {
  /** This row's share of the invoice's tax, in dollars. */
  tax: number
  /** This row's share of the invoice's shipping, in dollars. */
  shipping: number
  /** The vendor's own variance — at most three cents, either way — placed on
      this row because it is the largest (D-151). `0` on every other row, and on
      every row when no invoice total was passed or the variance is too big to
      reconcile. */
  adjustment: number
  /** `total + tax + shipping + adjustment`, rounded to the cent. */
  total: number
}

/**
 * Distribute `taxDollars` and `shippingDollars` across `rows`, returning a NEW
 * array — same length and order as `rows`, never mutating it — where each row
 * keeps its own fields and gains `tax`, `shipping`, `adjustment` and a
 * charge-inclusive `total`.
 *
 * **THE GRAIN RELATIONSHIP INVERTED 2026-08-19 (`docs/decisions.md` D-40).** The
 * Point Click Care export now allocates across LINE ITEMS, not GL-code
 * groups (the owner reversed the CSV's grain so Description could carry each
 * line's own text). So:
 *
 * THE ROWS ARE LINE ITEMS — what both line-item tables pass, and what the
 * GL Summary passes through `distributeByGroup` — so `total` is the SAME
 * figure the export will carry for that row, PROVIDED `rows` is the
 * invoice's FULL pool of lines: every company it touches, not one company's
 * subset (`docs/export.md`: spreading within a
 * subset would spread each whole charge again on a split invoice and
 * over-collect by exactly the charges).
 *
 * NEVER PASS GROUPS. Spreading over GL-code (or Account) groups puts the
 * remainder cents and the vendor's variance on the largest GROUP, while the
 * CSV puts them on the largest LINE, so a group could be off by up to three
 * cents plus a remainder cent (owner, 2026-09-25: "The CSV file produced at
 * Export Control should match the values decided inside the modal"). A
 * rollup spreads at line grain first and adds up afterwards —
 * `distributeByGroup` below — so every group carries exactly the sum of its
 * lines' CSV rows.
 *
 * **THE REMAINDER GOES ON THE ROW WITH THE LARGEST TOTAL** (owner, 2026-09-20
 * — `docs/decisions.md` D-129), which is `largestBaseIndex`'s pick and the
 * same row the export puts it on.
 *
 * **SO DOES THE VENDOR'S VARIANCE** (owner, 2026-09-25 — D-151). Pass the
 * invoice's printed `invoiceTotalDollars` and, when the rows plus both charges
 * miss it by at most three cents, the difference lands on that same largest
 * row — exactly what `exportBuilder.ts` does — so the distributed rows sum to
 * the printed Total and show the cents the CSV will carry. A bigger miss is
 * left alone: the Reconciles pill is red and the export refuses it.
 */
export function distributeCharges<T extends DistributableRow>(
  rows: readonly T[],
  taxDollars: number,
  shippingDollars: number,
  invoiceTotalDollars?: number | null,
): (T & ChargeShare)[] {
  if (rows.length === 0) return []
  const bases = rows.map((r) => r.total)
  const sequences = rows.map((r) => r.sequence ?? 0)
  const taxShares = allocateChargeDollars(taxDollars, bases, sequences)
  const shippingShares = allocateChargeDollars(shippingDollars, bases, sequences)
  const out = rows.map((r, i) => {
    const tax = taxShares[i] ?? 0
    const shipping = shippingShares[i] ?? 0
    return { ...r, tax, shipping, adjustment: 0, total: roundToCents(r.total + tax + shipping) }
  })
  if (invoiceTotalDollars !== undefined && invoiceTotalDollars !== null && Number.isFinite(invoiceTotalDollars)) {
    const emittedCents = out.reduce((sum, r) => sum + Math.round(r.total * 100), 0)
    const varianceCents = Math.round(invoiceTotalDollars * 100) - emittedCents
    const winner = out[largestBaseIndex(bases, sequences)]
    if (varianceCents !== 0 && reconcilesCents(varianceCents) && winner) {
      winner.adjustment = varianceCents / 100
      winner.total = roundToCents(winner.total + winner.adjustment)
    }
  }
  return out
}

/**
 * THE GL SUMMARY'S DISTRIBUTED FIGURES, AT THE CSV'S OWN GRAIN (owner,
 * 2026-09-25 — `docs/decisions.md` D-151: "The CSV file produced at Export
 * Control should match the values decided inside the modal"). Spreads both
 * charges and the vendor's variance across the LINES exactly as
 * `distributeCharges` (and so `exportBuilder.ts`) does, THEN adds the
 * charge-inclusive line totals up by `keyOf` — so each group's figure is the
 * exact sum of the CSV rows its lines become, to the cent.
 *
 * Returns one entry per distinct key, in dollars. Sums in whole cents so float
 * noise cannot move a group by a cent. `lines` must be the invoice's full pool
 * of lines (every company), for the reason `distributeCharges` states.
 */
export function distributeByGroup<T extends DistributableRow>(
  lines: readonly T[],
  keyOf: (line: T) => string,
  taxDollars: number,
  shippingDollars: number,
  invoiceTotalDollars?: number | null,
): Map<string, number> {
  const cents = new Map<string, number>()
  const distributed = distributeCharges(lines, taxDollars, shippingDollars, invoiceTotalDollars)
  distributed.forEach((d, i) => {
    const key = keyOf(lines[i] as T)
    cents.set(key, (cents.get(key) ?? 0) + Math.round(d.total * 100))
  })
  const out = new Map<string, number>()
  for (const [key, c] of cents) out.set(key, c / 100)
  return out
}

/**
 * The "Invoice Total:" a distributed view shows under its rows: the lines plus
 * both charges, plus the vendor's variance when it is within three cents of the
 * printed Total (D-151) — i.e. exactly what `distributeCharges`' rows sum to,
 * and what the CSV's rows sum to. A bigger miss is shown as it is, unadjusted.
 */
export function distributedGrandTotal(
  lineItemsSum: number,
  taxDollars: number,
  shippingDollars: number,
  invoiceTotalDollars?: number | null,
): number {
  const emittedCents =
    Math.round(lineItemsSum * 100) + Math.round(taxDollars * 100) + Math.round(shippingDollars * 100)
  if (invoiceTotalDollars === undefined || invoiceTotalDollars === null || !Number.isFinite(invoiceTotalDollars)) {
    return emittedCents / 100
  }
  const totalCents = Math.round(invoiceTotalDollars * 100)
  return (reconcilesCents(totalCents - emittedCents) ? totalCents : emittedCents) / 100
}

/** Dollars, rounded to the cent — kills the float noise from adding a
    cents-derived share to a summed dollar total (e.g. `37 + 2.71` can land on
    `39.709999999999994`). */
function roundToCents(n: number): number {
  return Math.round(n * 100) / 100
}
